import { FormSectionHandler } from '../entity.handler';
import { OverspeedProfileResponse, OverspeedProfileUpdateRequest, OverspeedBandItem } from '@key-telematics/fleet-api-client';
import { AdminEditorService } from '../../admin.service';
import { FormBuilderField } from 'app/shared/components/form-builder';
import { TextField, EntityUtils, NumberField } from '../utils';
import { ICONS } from '../icons';
import { ListFieldValue } from 'app/shared/components/form-builder/list/list.component';
import * as uuid from 'uuid';

interface EditorValues {
    name?: string;
    items?: ListFieldValue<OverspeedBandItem>[];
}

export class OverspeedProfileDetailsSectionHandler implements FormSectionHandler<OverspeedProfileResponse, EditorValues> {

    type = 'form';
    icon = ICONS.overspeedprofile;
    bulk = false;

    title = this.translate('TITLE');
    description = this.translate('DESC');
    warning = null;

    constructor(
        public admin: AdminEditorService,
        public ownerId: string,
        private creating: boolean
    ) {
    }

    translate(key: string, values?: Object): string {
        return this.admin.translate(`EDITORS.OVERSPEEDPROFILE.DETAILS.${key}`, values);
    }

    applyUpdate(entity: OverspeedProfileResponse, payload: OverspeedProfileUpdateRequest): Promise<OverspeedProfileResponse> {
        return this.admin.applyUpdate(api => api.entities.updateOverspeedProfile(entity.id, payload));
    }

    mapEntityToEditor(entity: OverspeedProfileResponse): Promise<EditorValues> {

        this.warning = entity.items && entity.items.length > 0 ? null : this.admin.translate(`EDITORS.OVERSPEEDPROFILE.WARNINGS.ITEM_LENGTH`);

        const result = {
            name: entity.name,
            items: this.stateItemsToListItems(entity.items),
        };
        Object.keys(result).forEach(key => result[key] === undefined && delete result[key]); // strip undefined properties from the object
        return Promise.resolve(result);
    }

    mapEditorToEntity(entity: OverspeedProfileUpdateRequest, values: EditorValues): Promise<OverspeedProfileUpdateRequest> {
        const patch: OverspeedProfileUpdateRequest = {
            name: values.name,
            items: values.items ? values.items.map(x => x.data) : undefined,
        };
        Object.keys(patch).forEach(key => patch[key] === undefined && delete patch[key]); // strip undefined properties from the object
        return Promise.resolve(EntityUtils.mergeFields(['name', 'items'], patch, entity));
    }

    getFields(entity: OverspeedProfileResponse): Promise<FormBuilderField[]> {
        return Promise.resolve().then(() => {
            return [
                TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                {
                    id: 'items', title: this.translate('FIELDS.ITEMS'), type: 'list', required: false, visible: !this.creating,
                    actions: [
                        { name: this.admin.i18n.instant('SHARED.ADD'), click: this.showFilterModal.bind(this), },
                        { id: 'edit', click: this.showFilterModal.bind(this), },
                    ],
                    getText: (field: FormBuilderField, values: any) => values[field.id] && values[field.id].map(x => x.name).join(', '),
                },
            ];
        }).then(fields => EntityUtils.prepareBulk(!entity, [], fields));

    }

    stateItemToListItem(item: OverspeedBandItem, max?: number): ListFieldValue<OverspeedBandItem> {
        return {
            id: item.id,
            name: item.name,
            icon: ICONS.overspeedprofile,
            description: this.translate(max ? 'FIELDS.DESC_MIN_AND_MAX' : 'FIELDS.DESC_MIN_ONLY', { min: item.min - 100, max: (max || 100) - 100 }),
            data: item,
        };
    }

    stateItemsToListItems(items: OverspeedBandItem[]): ListFieldValue<OverspeedBandItem>[] {
        return (items || []).sort((a, b) => {
            return a.min - b.min;
        }).map((x, idx, arr) => {
            const nextItem = idx < items.length - 1 ? arr[idx + 1] : null;
            const max = nextItem && nextItem.min;
            return this.stateItemToListItem(x, max);
        });
    }

    updateOrAddItem(items: ListFieldValue<OverspeedBandItem>[], existingItem: ListFieldValue<OverspeedBandItem>, item: OverspeedBandItem) {
        const stateItems = items.map(x => x.data);

        if (existingItem) {
            const idx = stateItems.findIndex(x => x.id === existingItem.id);
            stateItems.splice(idx, 1);
        }

        stateItems.push(item || existingItem.data);
        return this.stateItemsToListItems(stateItems);
    }

    showFilterModal(_action: string, _field: FormBuilderField, values: EditorValues, item: ListFieldValue<any>): Promise<OverspeedBandItem> {
        const model = (item && { ...item.data }) || { // pass some defaults for new items
            id: uuid.v4(),
            name: '',
            min: 100,
            offset: 0,
        };

        model.min -= 100;
        model.offset = this.admin.units.fromBackend('speed', model.offset, 2)

        return this.admin.showFormModal<OverspeedBandItem>({
            groups: [{
                name: this.translate('MODALS.STATE.TITLE'),
                description: this.translate('MODALS.STATE.DESC'),
                fields: [
                    TextField('name', this.translate('FIELDS.NAME'), true, 1, 255),
                    NumberField('min', this.translate('FIELDS.MIN'), true, 0, null, '%'),
                    NumberField('offset', this.translate('FIELDS.OFFSET'), true, -50, 50, this.admin.units.unit('speed')),
                ],
            }],
        }, model).then(result => {
            if (result) {
                result.min += 100;
                result.offset = this.admin.units.toBackend('speed', result.offset) as number
                values.items = this.updateOrAddItem(values.items, item, result);
            }
            return result;
        });



    }

}
